// frontend/src/components/dashboard/modals/AdminChangeLogModal.tsx
import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  UserGroupIcon,
  ClockIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { ChangeLog, changeLogService } from '../../../services/changeLog.service';
import { AuthUser } from '../../../services/auth.service';
import styles from './AdminChangeLogModal.module.css';

interface AdminChangeLogModalProps {
  changeLog: ChangeLog;
  user: AuthUser;
  onAcknowledge: () => void;
}

const AdminChangeLogModal: React.FC<AdminChangeLogModalProps> = ({
  changeLog,
  user,
  onAcknowledge
}) => {
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = user?.employeeNo || user?.employeeNumber || user?.uid || '';

  // Handle "Don't Show Me Again" - saves acknowledgment
  const handleDontShowAgain = async () => {
    try {
      setIsAcknowledging(true);
      setError(null);

      const success = await changeLogService.acknowledgeChangeLog(
        userId,
        changeLog.id,
        changeLog.version
      );

      if (success) {
        console.log('✅ User selected "Don\'t show me again"');
        setTimeout(() => {
          onAcknowledge();
        }, 300);
      } else {
        setError('Failed to save preference. Please try again.');
      }
    } catch (err) {
      console.error('Error saving preference:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsAcknowledging(false);
    }
  };

  // Handle "Close" - just closes modal, will show again next time
  const handleClose = () => {
    console.log('📋 User closed modal - will show again next time');
    onAcknowledge();
  };

  const daysRemaining = Math.ceil(
    (changeLog.expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  // Define the system issues
  const systemIssues = [
    {
      id: 'mobile-app-registration',
      title: 'Employee Registration for Mobile App',
      description: 'Several employees have been added to the main HR portal but have not yet been set up in the mobile attendance app. These employees are unable to register and mark their attendance through the app.',
      action: 'Please ensure all active employees in the HR portal are also added to the mobile app system.',
      icon: <UserGroupIcon className={styles.issueIcon} />,
      color: '#ef4444',
      priority: 'High'
    },
    {
      id: 'inactive-employees',
      title: 'Inactive Employee Removal',
      description: 'Employees who are no longer with Phoenician Technical Services are still present in the system. This is affecting our attendance accuracy metrics and making it difficult to identify actual problems and track improvements.',
      action: 'Please remove all inactive employees from both the portal and the app to ensure data accuracy.',
      icon: <ShieldExclamationIcon className={styles.issueIcon} />,
      color: '#f59e0b',
      priority: 'Medium'
    },
    {
      id: 'overtime-eligibility',
      title: 'Overtime Eligibility List Update',
      description: 'Some employees who are present and working cannot be added for overtime requests by their engineers. This is because these employee names are missing from the overtime eligibility data list.',
      action: 'Please review and update the overtime-eligible employees list so engineers can properly submit overtime requests for their team members.',
      icon: <ClockIcon className={styles.issueIcon} />,
      color: '#3b82f6',
      priority: 'Medium'
    }
  ];

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.warningBadge}>
            <ExclamationTriangleIcon className={styles.warningIcon} />
            <span>SYSTEM ALERTS</span>
          </div>
          
          <h1 className={styles.modalTitle}>
            Important HR System Updates Required
          </h1>
          
          <p className={styles.modalSubtitle}>
            Please review and address the following issues to ensure smooth operations
          </p>

          {daysRemaining > 0 && (
            <div className={styles.expiryNotice}>
              This notice will be shown for the next {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Issues List */}
        <div className={styles.issuesContainer}>
          <h2 className={styles.issuesTitle}>Action Items</h2>
          
          <div className={styles.issuesList}>
            {systemIssues.map((issue, index) => (
              <div 
                key={issue.id} 
                className={styles.issueItem}
                style={{ 
                  borderLeftColor: issue.color,
                  background: `linear-gradient(135deg, ${issue.color}08, ${issue.color}03)`
                }}
              >
                <div className={styles.issueHeader}>
                  <div className={styles.issueIconContainer} style={{ color: issue.color }}>
                    {issue.icon}
                  </div>
                  
                  <div className={styles.issueTitleContainer}>
                    <div className={styles.issueTitleRow}>
                      <h3 className={styles.issueTitle}>{issue.title}</h3>
                      <span 
                        className={styles.priorityBadge}
                        style={{ 
                          background: issue.color,
                          color: 'white'
                        }}
                      >
                        {issue.priority}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className={styles.issueContent}>
                  <p className={styles.issueDescription}>
                    <strong>Issue:</strong> {issue.description}
                  </p>
                  
                  <p className={styles.issueAction}>
                    <strong>Required Action:</strong> {issue.action}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Important Notice */}
        <div className={styles.importantNotice}>
          <ExclamationTriangleIcon className={styles.noticeIcon} />
          <div>
            <strong>Please Note:</strong> These issues require attention to maintain system accuracy 
            and ensure all employees can properly access and use the attendance system. 
            This notice will appear for the next {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.
          </div>
        </div>

        {/* Error Message (if any) */}
        {error && (
          <div className={styles.errorMessage}>
            <ExclamationTriangleIcon className={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.modalFooter}>
          <div className={styles.buttonGroup}>
            {/* Close Button - Will show again */}
            <button
              onClick={handleClose}
              disabled={isAcknowledging}
              className={styles.closeButton}
            >
              <XMarkIcon className={styles.buttonIcon} />
              Close
            </button>

            {/* Don't Show Again Button - Saves acknowledgment */}
            <button
              onClick={handleDontShowAgain}
              disabled={isAcknowledging}
              className={styles.acknowledgeButton}
            >
              {isAcknowledging ? (
                <>
                  <div className={styles.spinner}></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className={styles.buttonIcon} />
                  Don't Show Me Again
                </>
              )}
            </button>
          </div>

          <p className={styles.footerNote}>
            Click "Close" to see this again next time, or "Don't Show Me Again" to hide permanently.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminChangeLogModal;